import type { GrowlerCart, GrowlerCartItem } from 'utils/types/GrowlerCart';
import type { Growler } from 'utils/types/Growler';

const STORAGE_KEY = 'clube42-growlers-cart';
const MAX_AGE_DAYS = 7;
const STORAGE_VERSION = '1.0';

interface StoredGrowlerCart extends GrowlerCart {
  version: string;
  lastUpdated: string;
}

export class GrowlerCartStorage {
  static saveCart(cart: GrowlerCart): void {
    try {
      const storedCart: StoredGrowlerCart = {
        ...cart,
        version: STORAGE_VERSION,
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedCart));
    } catch (error) {
      console.error('[GrowlerCartStorage] Erro ao salvar carrinho:', error);
    }
  }

  static loadCart(): GrowlerCart | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const parsedCart: StoredGrowlerCart = JSON.parse(stored);

      if (parsedCart.version !== STORAGE_VERSION) {
        GrowlerCartStorage.clearCart();
        return null;
      }

      const lastUpdated = new Date(parsedCart.lastUpdated);
      const maxAge = new Date();
      maxAge.setDate(maxAge.getDate() - MAX_AGE_DAYS);

      if (lastUpdated < maxAge) {
        GrowlerCartStorage.clearCart();
        return null;
      }

      const validItems = GrowlerCartStorage.validateAndCleanItems(
        parsedCart.items,
      );

      return GrowlerCartStorage.recalculateTotals({
        ...parsedCart,
        items: validItems,
      });
    } catch (error) {
      GrowlerCartStorage.clearCart();
      return null;
    }
  }

  static clearCart(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('[GrowlerCartStorage] Erro ao limpar carrinho:', error);
    }
  }

  private static validateAndCleanItems(
    items: GrowlerCartItem[],
  ): GrowlerCartItem[] {
    const maxItemAge = new Date();
    maxItemAge.setDate(maxItemAge.getDate() - MAX_AGE_DAYS);

    return items.filter(item => {
      if (
        !item.growler ||
        !item.growler.id ||
        !item.growler.name ||
        item.quantity <= 0
      ) {
        return false;
      }

      const addedAt = new Date(item.addedAt);
      if (addedAt < maxItemAge) {
        return false;
      }

      if (!item.growler.inStock) {
        return false;
      }

      return GrowlerCartStorage.validateGrowler(item.growler);
    });
  }

  private static recalculateTotals(
    cart: Omit<GrowlerCart, 'totalItems' | 'totalValue'>,
  ): GrowlerCart {
    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalValue =
      Math.round(
        cart.items.reduce(
          (sum, item) => sum + item.growler.price * item.quantity,
          0,
        ) * 100,
      ) / 100;

    return {
      ...cart,
      totalItems,
      totalValue,
    };
  }

  static validateGrowler(growler: Growler): boolean {
    if (!growler.id || !growler.name || !growler.brewery || !growler.price) {
      return false;
    }

    return growler.price > 0;
  }
}
